import React from 'react';
import styled from 'styled-components';

import Text from '../basic/Text';
import Button from '../basic/Button';
import Section from '../basic/layouts/Section';
import Video from './Video';
import WistiaVideo from './WistiaVideo';

import ArrowImage from '../../images/icons/Hero-Arrow.svg';
import theme from '../../styles/theme';
import HTMLWrapper from './HTMLWrapper';
import documentToReactComponents from '../util/documentToReactComponents';

const {
	colors,
	breakPoints: { screenQuery },
} = theme;

const Hero = styled(Section)`
	text-align: ${props =>
		props.textAlign
			? props.textAlign
			: !props.arrow && !props.video && !props.image
				? `center`
				: `left`};
	display: grid;
	grid-template-columns: ${props =>
		props.video
			? `1fr 1fr`
			: props.arrow || props.image || props.spanTwoThirds
				? `2fr 1fr`
				: `1fr`};
	/* grid-template-columns: repeat(12, 1fr); */
	grid-gap: 60px;
	align-items: center;
	padding: ${props => props.padding && props.padding};

	p {
		text-align: ${props =>
			props.textAlign
				? props.textAlign
				: !props.arrow && !props.video && !props.image
					? `center`
					: `left`};
	}
	@media screen and (max-width: 1223px) {
		${props =>
			props.video
				? `grid-template-columns: 1fr; text-align: center;  grid-gap: 75px;`
				: null}
	}
	${screenQuery.large} {
		text-align: ${props => (props.image ? `left` : `center`)};
		grid-template-columns: ${props => (props.image ? `2fr 1fr` : `1fr`)};
	}
	${screenQuery.medium} {
		text-align: center;
		grid-gap: 60px;
		grid-template-columns: 1fr;
	}
`;
const MainSection = styled.div`
	${props =>
		props.maxHeaderWidth
			? `max-width: ${props.maxHeaderWidth}; margin: 0 auto;`
			: ''}
`;
const ButtonWrapper = styled.div`
	margin-top: 60px;
	display: grid;
	grid-template-columns: repeat(2, auto);
	grid-column-gap: 30px;
	grid-row-gap: 20px;
	justify-content: flex-start;
	/*for anchor link buttons: */
	> a div {
		width: 100%;
	}
	margin-left: auto;
	margin-right: auto;
	@media screen and (max-width: 1223px) {
		${props => (props.video ? `max-width: 80%;` : null)}
		${props =>
			props.video && props.numberOfButtons === 2
				? `grid-template-columns: repeat(2, minmax(230px, 1fr))`
				: null};
		${props =>
			props.video && props.numberOfButtons === 1
				? `grid-template-columns: repeat(1, minmax(230px, 330px))`
				: null};
	}
	${screenQuery.large} {
		max-width: 80%;
		${props =>
			props.numberOfButtons === 2
				? `grid-template-columns: repeat(2, minmax(230px, 1fr))`
				: null};
		${props =>
			props.numberOfButtons === 1
				? `grid-template-columns: repeat(1, minmax(230px, 330px))`
				: null};
		grid-column-gap: 40px;
	}
	${screenQuery.small} {
		grid-template-columns: 260px;
		margin-left: auto;
		margin-right: auto;
	}
`;
const Arrow = styled.img`
	height: 200px;
	justify-self: center;
	transform: translateY(120%);
	${screenQuery.large} {
		display: none;
	}
`;
const VideoWrapper = styled.div`
	@media screen and (max-width: 1223px) {
		width: 80%;
		margin-left: auto;
		margin-right: auto;
	}
`;
const Overline = styled(Text)`
	font-size: 14px;
	line-height: 19px;
	text-transform: uppercase;
	margin-bottom: 28px;
	letter-spacing: 0.8px;
	max-width: 400px;
	${props =>
		props.arrow || props.image
			? ``
			: `
        margin-left: auto;
        margin-right: auto;
    `}
	@media screen and (max-width: 1223px) {
		${props =>
			props.video
				? ` margin-left: auto;
        margin-right: auto;`
				: null}
	}
	${screenQuery.large} {
		${props => (props.image ? `` : `margin: 0 auto 28px;`)}
	}
	${screenQuery.medium} {
		margin: 0 auto 18px;
	}
`;
const HeroImage = styled.img(
	props => `
    width: ${props.normalizeHeroWidth ? 'auto' : '100%'};
    max-height: 300px;
    ${props.maxWidth ? `max-width: ${props.maxWidth}px;` : ``}

`
);

export default ({
	overline,
	header,
	subheader,
	longSubheader,
	rawSubheader,
	buttons,
	arrow,
	image,
	normalizeHeroWidth,
	heroImageMaxWidth,
	hideButtons,
	videoId,
	backgroundColor,
	ABTest,
	children,
	maxHeaderWidth,
	noTopPadding,
	noBottomPadding,
	...props
}) => {
	// if subheader has a pipe, split into 2 paragraphs:
	if (typeof subheader === 'string' && subheader?.indexOf('|') >= 0) {
		subheader = subheader.split('|');
	}

	return (
		<Hero
			key="page-hero"
			backgroundColor={
				backgroundColor ? backgroundColor : colors.grayscale.white
			}
			arrow={arrow}
			video={videoId}
			image={image}
			showOverflow
			className="hero-container"
			noTopPadding={noTopPadding}
			noBottomPadding={noBottomPadding}
			{...props}
		>
			<MainSection
				arrow={arrow}
				video={videoId}
				maxHeaderWidth={maxHeaderWidth}
			>
				{overline ? (
					<Overline
						arrow={arrow}
						image={image}
						video={videoId}
						semiBold
						component="b4"
						className="overline"
					>
						{overline}
					</Overline>
				) : null}
				<Text component={props.headerElement || 'h1'}>{header}</Text>
				{subheader && typeof subheader === 'object'
					? subheader?.map((text, index) => (
							<Text
								css={index !== subheader.length - 1 && { marginBottom: 20 }}
								component={props.subheaderElement || 'b1'}
							>
								{text}
							</Text>
						))
					: subheader && (
							<Text component={props.subheaderElement || 'b1'}>
								{subheader}
							</Text>
						)}
				{rawSubheader && (
					<Text component={props.subheaderElement || 'b1'}>
						{documentToReactComponents(rawSubheader)}
					</Text>
				)}
				{longSubheader && (
					<Text component={props.subheaderElement || 'b1'}>
						<HTMLWrapper
							html={longSubheader?.childMarkdownRemark?.html || longSubheader}
						/>
					</Text>
				)}
				{!hideButtons && buttons?.length > 0 ? (
					<ButtonWrapper
						spanTwoThirds={props.spanTwoThirds}
						buttonCount={buttons?.length}
						arrow={arrow}
						video={videoId}
						numberOfButtons={buttons?.length}
						className="hero-button-wrapper"
					>
						{buttons.map((button, i) => (
							<Button
								secondary={i % 2 === 1}
								key={button.buttonText}
								onClick={button.onClick}
								anchorLink={button.buttonAnchor}
								link={button.buttonLink}
								arrow
							>
								{button.buttonText}
							</Button>
						))}
					</ButtonWrapper>
				) : null}
				{children && <div>{children}</div>}
			</MainSection>
			{videoId ? (
				<VideoWrapper>
					<WistiaVideo key={videoId} videoId={videoId} ABTest={ABTest} />
				</VideoWrapper>
			) : null}
			{arrow && !videoId ? (
				<Arrow className="hero-arrow" src={ArrowImage} alt="arrow" />
			) : null}
			{image && !videoId ? (
				<HeroImage
					className="hero-image"
					src={image}
					alt="Hero Image"
					maxWidth={heroImageMaxWidth}
					normalizeHeroWidth={normalizeHeroWidth}
				/>
			) : null}
		</Hero>
	);
};
